import React, {useContext, useEffect, useState} from "react";
import {CircularProgress, Grid, makeStyles, Typography} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import Box from "@material-ui/core/Box";
import StreamlineIcon from "../../widget/streamline-icon/StreamlineIcon";
import {retrieveContentfulImage} from "../../../common/image";
import Bin2Alternate from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/delete/bin-2-alternate.svg";
import CheckCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/form-validation/check-circle.svg";
import AccessibleIcon from "../../widget/streamline-icon/AccessibleIcon";
import PropTypes from "prop-types";
import {useApartmentSearch, useBreakpoints, useResortDetail, useWaitingList} from "../../../hooks";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {useDate, useI18N, useLocation} from "../../../common/common";
import {enDash} from "../../../common/constant";
import {calculateDays} from "../../../common/date";
import CustomDialog from "../../dialog/marketplace/common/CustomDialog";
import {useWaitingListApartments} from "../../../hooks/useWaitingListApartments";
import AddIcon from "../../widget/streamline-icon/AddIcon";
import clsx from "clsx";
import {apartmentType} from "../../../types/ApartmentTypes";
import CustomSliderArrow from "../../widget/button/CustomSliderArrow";
import * as ROUTES from "../../../common/routes";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";

const useStyles = makeStyles(theme => ({
    apartmentRoot: {
        marginTop: theme.spacing(2),
        minHeight: 114,
        overflow: "hidden"
    },
    loadingImage: {
        height: "100%",
        width: "100%",
        display: "block",
        objectFit: "cover",
    },
    flex1: {
        flex: 1
    },
    apartmentContainer: {
        height: "100%",
        border: "1px solid " + theme.palette.borders.line.light,
        borderRadius: theme.spacing(0.75),
        overflow: "hidden"
    },
    imageWrapper: {
        position: "relative",
        width: "45%",
    },
    iconBox: {
        borderRadius: theme.spacing(0.5),
        border: `1px solid ${theme.palette.text.white}`,
        background: theme.palette.text.link,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: theme.spacing(3.5),
        height: theme.spacing(3.5),
        "& i": {
            color: theme.palette.text.white,
            fontSize: theme.spacing(2)
        }
    },
    deleteIcon: {
        marginLeft: theme.spacing(1),
        color: theme.palette.error.danger,
        cursor: "pointer",
        [theme.breakpoints.up("sm")]: {
            marginLeft: theme.spacing(2.5),
        }
    },
    addIcon: {
        marginLeft: theme.spacing(1),
        color: theme.palette.primary.main,
        cursor: "pointer",
        [theme.breakpoints.up("sm")]: {
            marginLeft: theme.spacing(2.5),
        }
    },
    circularProgress: {
        marginLeft: theme.spacing(1),
        color: theme.palette.primary.main,
        [theme.breakpoints.up("sm")]: {
            marginLeft: theme.spacing(2.5),
        }
    },
    infoWrapper: {
        height: "100%",
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        [theme.breakpoints.up("sm")]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        }
    },
    displayBlock: {
        display: "block",
    },
    mb1: {
        marginBottom: theme.spacing(0.5),
        [theme.breakpoints.up("sm")]: {
            marginBottom: theme.spacing(1),
        }
    },
    height100: {
        height: "100%"
    }
}));

const WaitingListApartment = ({
    resortId,
    apartmentShortCode,
    numberOfGuests,
    waitingListId,
    dateFrom,
    dateTo,
    numberOfNights,
    onDelete,
    onAdd,
    onSendAnalytics,
    type,
    apartmentObject,
    isLast
}) => {
    const classes = useStyles();
    const {i18n, i18nLowerIfRequired} = useI18N();
    const {localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const {isExtraSmallScreen} = useBreakpoints();
    const {formatDate} = useDate();
    const {loadApartments, loading, apartmentsList} = useApartmentSearch();
    const {navigate} = useLocation();
    const {sendCustomEvent, eventNames, getResortAndApartmentData} = useAnalytics();
    const {
        deleteApartment,
        loadingDeleteApartment,
        errorDeleteApartment,
        setErrorDeleteApartment,
        addApartment,
        loadingAddApartment
    } = useWaitingListApartments();
    const {
        deleteWaitingList,
        deletingWaitingListLoading,
        deleteWaitingListError
    } = useWaitingList();

    const [openDeleteApartmentDialog, setOpenDeleteApartmentDialog] = useState(false);
    const [removeAddButton, setRemoveAddButton] = useState(false);
    const [apartment, setApartment] = useState(apartmentObject || undefined);
    const {resortDetails} = useResortDetail(resortId);

    useEffect(() => {
        if (!apartmentObject) {
            loadApartments({
                resort_id: resortId,
                apartment_short_code: apartmentShortCode,
                number_of_guests: numberOfGuests,
                wheelchair_accessible: "false", // this will not be taken into account by the endpoint but it is required
                locale: localeUppercaseUnderscore,
            });
        }
    }, [apartmentObject]);

    useEffect(() => {
        if (apartmentsList && apartmentsList?.length) {
            setApartment(apartmentsList[0]);
        }
    }, [apartmentsList]);

    const handleOpenDeleteDialog = () => {
        setOpenDeleteApartmentDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setErrorDeleteApartment(null);
        setOpenDeleteApartmentDialog(false);
    };

    const handleAddApartment = () => {
        const apartmentName = apartment?.apartment_size_name + " " + apartment?.apartment_classification;
        addApartment(waitingListId, apartmentShortCode).then(data => {
            if (data?.status === 200) {
                setRemoveAddButton(true);
                onSendAnalytics(apartment, resortDetails, type);
                onAdd("success", apartmentName);
            } else {
                onAdd("warning");
            }
        });
    };

    const handleDeleteApartment = () => {
        if (!isLast) {
            deleteApartment(waitingListId, apartmentShortCode).then(data => {
                if (data?.status === 200) {
                    setOpenDeleteApartmentDialog(false);
                    const apartmentName = apartment?.apartment_size_name + " " + apartment?.apartment_classification;
                    onDelete(apartmentShortCode, apartmentName, apartment, resortDetails);
                    sendCustomEvent({
                        event_name: eventNames.WAITING_LIST_REMOVE_ENTRY,
                        [eventNames.WAITING_LIST_REMOVE_ENTRY]: getResortAndApartmentData(resortDetails, apartment, numberOfGuests)
                    });
                    onSendAnalytics(apartment, resortDetails, type);
                }
            });
        }
        else {
            deleteWaitingList(waitingListId)
                .then(() => {
                    sendCustomEvent({
                        event_name: eventNames.WAITING_LIST_REMOVE_ENTRY,
                        [eventNames.WAITING_LIST_REMOVE_ENTRY]: getResortAndApartmentData(resortDetails, apartment, numberOfGuests)
                    });
                    onSendAnalytics(apartment, resortDetails, type);
                    sendCustomEvent({
                        event_name: eventNames.WAITING_LIST_DELETION_CONFIRMED,
                        [eventNames.WAITING_LIST_DELETION_CONFIRMED]: getResortAndApartmentData(resortDetails)
                    });
                })
                .finally(() => {
                    navigate(ROUTES.RESERVATIONS_WAITING_LIST + "/?deleted=true");
                });
        }
    };

    return (
        <Grid
            container
            direction={"row"}
            wrap={"nowrap"}
            className={classes.apartmentRoot}
        >
            {loading && !apartment ? (
                <Skeleton
                    variant={"rect"}
                    height={114}
                    classes={{root: classes.loadingImage}}
                />
            ) : (
                <React.Fragment>
                    <Grid
                        container
                        direction={"row"}
                        alignItems={"center"}
                    >
                        <div
                            className={clsx(classes.flex1, classes.height100)}
                        >
                            <Grid
                                container
                                className={classes.apartmentContainer}
                            >
                                <div className={classes.imageWrapper}>
                                    {apartment?.apartment_picture && (
                                        <img
                                            src={retrieveContentfulImage(apartment?.apartment_picture, {
                                                width: 209,
                                                height: 114
                                            })}
                                            alt={apartment?.apartment_size_name + " " + apartment?.apartment_classification || ""}
                                            className={classes.loadingImage}
                                        />
                                    )}

                                    {apartment?.wheelchair_accessibility && (
                                        <Box
                                            className={classes.iconBox}
                                            position="absolute"
                                            bottom={8}
                                            right={8}
                                        >
                                            <AccessibleIcon />
                                        </Box>
                                    )}
                                </div>

                                <div
                                    className={classes.flex1}
                                >
                                    <Grid
                                        container
                                        direction={"column"}
                                        justify={"center"}
                                        className={classes.infoWrapper}
                                    >
                                        <Grid item>
                                            <Typography
                                                variant="subtitle2"
                                                className={classes.mb1}
                                            >
                                                {!loading ? (
                                                    apartment?.apartment_size_name + " " +
                                                    apartment?.apartment_classification + ", " +
                                                    numberOfGuests + " " +
                                                    (numberOfGuests === 1 ? i18nLowerIfRequired("dialog.search_bar.search_date_guest.guest") : i18nLowerIfRequired("dialog.search_bar.search_date_guest.guests"))
                                                ) : <Skeleton />}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                className={clsx(classes.displayBlock, classes.mb1)}
                                            >
                                                {!loading ? formatDate(dateFrom, {weekday: "short", day: "numeric", month: "short"}) + " " + enDash + " " + formatDate(dateTo, {weekday: "short", day: "numeric", month: "short"}) : <Skeleton />}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                className={clsx(classes.displayBlock, classes.mb1)}
                                            >
                                                {loading ? <Skeleton /> : (
                                                    <>
                                                        {(numberOfNights || calculateDays(new Date(dateFrom), new Date(dateTo))) + " " + ((numberOfNights || calculateDays(new Date(dateFrom), new Date(dateTo))) > 1 ? i18nLowerIfRequired("common.label.nights") : i18nLowerIfRequired("common.label.night"))}
                                                    </>
                                                )}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                        </div>

                        <Grid
                            item
                        >
                            <div
                                tabIndex={0}
                                onKeyDown={(event) => {
                                    if (event.keyCode === 13 || event.keyCode === 32) {
                                        event.preventDefault();
                                        if (type === "delete") {
                                            handleOpenDeleteDialog();
                                        } else if (!loadingAddApartment && type === "add" && !removeAddButton) {
                                            handleAddApartment();
                                        }
                                    }
                                }}
                            >
                                {type === "delete" && (
                                    <StreamlineIcon
                                        className={classes.deleteIcon}
                                        onClick={handleOpenDeleteDialog}
                                    >
                                        <Bin2Alternate />
                                    </StreamlineIcon>
                                )}

                                {!loadingAddApartment && type === "add" && (
                                    !removeAddButton ? (
                                        <AddIcon
                                            className={classes.addIcon}
                                            onClick={handleAddApartment}
                                        />
                                    ) : (
                                        <StreamlineIcon
                                            data-testid="check-circle-icon"
                                            className={classes.addIcon}
                                        >
                                            <CheckCircle />
                                        </StreamlineIcon>
                                    )
                                )}
                                {loadingAddApartment && type === "add" && (
                                    <CircularProgress
                                        size={22}
                                        className={classes.circularProgress}
                                        thickness={4.5}
                                    />
                                )}
                            </div>
                        </Grid>
                    </Grid>

                </React.Fragment>
            )}

            <CustomDialog
                isOpen={openDeleteApartmentDialog}
                isMobile={isExtraSmallScreen()}
                isFullScreen={isExtraSmallScreen()}
                imgSrc={"/images/delete_offer.png"}
                imgAlt={"Delete apartment - Waiting list"}
                onCloseDialog={handleCloseDeleteDialog}
                onButtonClick={handleDeleteApartment}
                errorMessage={errorDeleteApartment || deleteWaitingListError ? i18n("common.label.generic_error") : null}
                buttonLoading={loadingDeleteApartment || deletingWaitingListLoading}
                buttonContained
                wrapperMarginTop
                isRedButton
                title={
                    <>
                        {i18n("dialog.waiting_list.delete_apartment.title")}
                    </>
                }
                cta={i18n("dialog.waiting_list.delete_apartment.cta")}
                secondButton
                buttonE2eId="cancel-delete-apartment-waiting-list-dialog-btn"
                secondButtonE2eId="delete-apartment-waiting-list-dialog-btn"
            />

        </Grid>
    );
};

CustomSliderArrow.defaultProps = {
    isLast: false,
};

WaitingListApartment.propTypes = {
    resortId: PropTypes.number.isRequired,
    apartmentShortCode: PropTypes.string.isRequired,
    numberOfGuests: PropTypes.number.isRequired,
    waitingListId: PropTypes.string.isRequired,
    dateFrom: PropTypes.string.isRequired,
    dateTo: PropTypes.string.isRequired,
    numberOfNights: PropTypes.number,
    onDelete: PropTypes.func,
    onAdd: PropTypes.func,
    onSendAnalytics: PropTypes.func,
    type: PropTypes.oneOf(["add", "delete"]).isRequired,
    apartmentObject: PropTypes.shape({
        ...apartmentType
    }),
    isLast: PropTypes.bool
};

export default WaitingListApartment;
